import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress,
  Grid,
  Avatar,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Chip,
  Paper,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchPosts } from "../features/postsSlice";
import { updateUserReducer, fetchUsers, followUserAsync, unfollowUserAsync } from "../features/usersSlice";
import ProfilePosts from "../components/ProfilePosts";
import CreatePost from "../components/CreatePost";
import { updateUser } from "../utils/api";
import AddFriends from "../components/AddFriends";
import OnlineFriends from "../components/OnlineFriends";

const InfoRow = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, py: 0.6 }}>
      <Box sx={{ color: "#D4AF37", display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography sx={{ fontSize: 13.5, color: "#6B7280", minWidth: 60 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5, color: "#1F2937", fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const { id } = useParams();
  const [followState, setFollowState] = useState("");
  const [followLoading, setFollowLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const { posts, isLoading: postsLoading } = useSelector((state) => state.posts);
  const { users, isLoading: usersLoading } = useSelector((state) => state.users);
  const visitorUser = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (posts.length === 0) dispatch(fetchPosts());
    if (users.length === 0) dispatch(fetchUsers());
  }, [dispatch]);

  const user = useMemo(() => users.find((u) => u._id === id), [users, id]);
  const userPosts = useMemo(
    () => posts.filter((post) => post.author._id === id),
    [posts, id]
  );

  const [updatedUser, setUpdatedUser] = useState(user || {});

  useEffect(() => {
    if (user && visitorUser) {
      setFollowState(
        visitorUser.following.includes(user._id) ? "Unfollow" : "Follow"
      );
    }
  }, [visitorUser, user]);

  useEffect(() => {
    if (user) setUpdatedUser(user);
  }, [user]);

  const handleChange = (e) => {
    setUpdatedUser({ ...updatedUser, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setLoadingSave(true);
    try {
      await updateUser(id, updatedUser);
      dispatch(updateUserReducer({ id, updatedUser: { ...updatedUser } }));
      setEditOpen(false);
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setLoadingSave(false);
    }
  };

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      if (followState === "Follow") {
        await dispatch(followUserAsync({ followerId: visitorUser._id, followingId: user._id }));
        setFollowState("Unfollow");
      } else {
        await dispatch(unfollowUserAsync({ followerId: visitorUser._id, followingId: user._id }));
        setFollowState("Follow");
      }
    } catch (error) {
      console.error("Error handling follow:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ bgcolor: "#F0F2F5", minHeight: "calc(100vh - 65px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#6B7280", fontSize: 16 }}>
          Sign in to view profiles.
        </Typography>
      </Box>
    );
  }

  if (usersLoading || postsLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "calc(100vh - 65px)", gap: 2, bgcolor: "#F0F2F5" }}>
        <CircularProgress sx={{ color: "#D4AF37" }} />
        <Typography sx={{ color: "#9CA3AF", fontSize: 14 }}>Loading profile…</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ bgcolor: "#F0F2F5", minHeight: "calc(100vh - 65px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#EF4444", fontSize: 16 }}>User not found.</Typography>
      </Box>
    );
  }

  const initials = `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase();
  const isOwner = visitorUser._id === id;

  return (
    <Box sx={{ bgcolor: "#F0F2F5", minHeight: "calc(100vh - 65px)", pb: 4 }}>
      <Container maxWidth="lg" sx={{ pt: 3 }}>

        {/* Profile header card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            mb: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.09)",
          }}
        >
          {/* Cover band */}
          <Box
            sx={{
              height: 90,
              background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 60%, #0F3460 100%)",
            }}
          />

          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
            {/* Avatar + actions row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                mt: -4,
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Avatar
                src={user.profilePicture}
                sx={{
                  width: 88,
                  height: 88,
                  border: "4px solid #fff",
                  bgcolor: "#D4AF37",
                  color: "#1A1A2E",
                  fontSize: 28,
                  fontWeight: 800,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                }}
              >
                {initials}
              </Avatar>

              <Box sx={{ display: "flex", gap: 1, pb: 0.5 }}>
                {!isOwner && (
                  <Button
                    variant={followState === "Follow" ? "contained" : "outlined"}
                    onClick={handleFollow}
                    disabled={followLoading}
                    startIcon={
                      followLoading
                        ? <CircularProgress size={14} color="inherit" />
                        : followState === "Follow"
                          ? <PersonAddAlt1RoundedIcon sx={{ fontSize: 17 }} />
                          : <PersonRemoveRoundedIcon sx={{ fontSize: 17 }} />
                    }
                    sx={{
                      borderRadius: "9px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13.5,
                      px: 2,
                      ...(followState === "Follow"
                        ? {
                            background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
                            "&:hover": { background: "linear-gradient(135deg, #D4AF37 0%, #b8941f 100%)", color: "#1A1A2E" },
                          }
                        : {
                            borderColor: "#1A1A2E",
                            color: "#1A1A2E",
                            "&:hover": { bgcolor: "rgba(26,26,46,0.06)" },
                          }),
                    }}
                  >
                    {followLoading ? "" : followState}
                  </Button>
                )}
                {isOwner && (
                  <Button
                    variant="outlined"
                    startIcon={<EditRoundedIcon sx={{ fontSize: 17 }} />}
                    onClick={() => setEditOpen(true)}
                    sx={{
                      borderRadius: "9px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13.5,
                      px: 2,
                      borderColor: "#1A1A2E",
                      color: "#1A1A2E",
                      "&:hover": { bgcolor: "rgba(26,26,46,0.06)" },
                    }}
                  >
                    Edit profile
                  </Button>
                )}
              </Box>
            </Box>

            {/* Name + stats */}
            <Typography sx={{ fontWeight: 800, fontSize: 22, color: "#111827", lineHeight: 1.2 }}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography sx={{ color: "#9CA3AF", fontSize: 13.5, mt: 0.3, mb: 1.5 }}>
              @{user.username}
            </Typography>

            <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#111827", lineHeight: 1 }}>
                  {userPosts.length}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#9CA3AF", mt: 0.3 }}>Posts</Typography>
              </Box>
              <Link to={`/profile/${id}/followers`} style={{ textDecoration: "none" }}>
                <Box
                  sx={{
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover .stat-num": { color: "#D4AF37" },
                    "&:hover .stat-label": { color: "#6B7280" },
                  }}
                >
                  <Typography className="stat-num" sx={{ fontWeight: 800, fontSize: 18, color: "#111827", lineHeight: 1, transition: "color 0.15s" }}>
                    {user.followers?.length ?? 0}
                  </Typography>
                  <Typography className="stat-label" sx={{ fontSize: 12, color: "#9CA3AF", mt: 0.3, transition: "color 0.15s" }}>Followers</Typography>
                </Box>
              </Link>
              <Link to={`/profile/${id}/following`} style={{ textDecoration: "none" }}>
                <Box
                  sx={{
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover .stat-num": { color: "#D4AF37" },
                    "&:hover .stat-label": { color: "#6B7280" },
                  }}
                >
                  <Typography className="stat-num" sx={{ fontWeight: 800, fontSize: 18, color: "#111827", lineHeight: 1, transition: "color 0.15s" }}>
                    {user.following?.length ?? 0}
                  </Typography>
                  <Typography className="stat-label" sx={{ fontSize: 12, color: "#9CA3AF", mt: 0.3, transition: "color 0.15s" }}>Following</Typography>
                </Box>
              </Link>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Info rows */}
            <Box>
              <InfoRow icon={<EmailOutlinedIcon sx={{ fontSize: 17 }} />} label="Email" value={user.email} />
              <InfoRow icon={<WorkOutlineRoundedIcon sx={{ fontSize: 17 }} />} label="Work" value={user.work} />
              <InfoRow icon={<SchoolOutlinedIcon sx={{ fontSize: 17 }} />} label="Education" value={user.education} />
              <InfoRow icon={<LocationOnOutlinedIcon sx={{ fontSize: 17 }} />} label="From" value={user.origin} />
              <InfoRow icon={<LocationOnOutlinedIcon sx={{ fontSize: 17 }} />} label="Lives in" value={user.currentLocation} />
              <InfoRow
                icon={<CakeOutlinedIcon sx={{ fontSize: 17 }} />}
                label="Birthday"
                value={user.birthDate ? new Date(user.birthDate).toLocaleDateString() : null}
              />
              <InfoRow icon={<FavoriteOutlinedIcon sx={{ fontSize: 17 }} />} label="Status" value={user.maritalStatus} />
            </Box>

            {/* Gender chip */}
            {user.gender && (
              <Box sx={{ mt: 1.5 }}>
                <Chip
                  label={user.gender}
                  size="small"
                  sx={{
                    bgcolor: "rgba(212,175,55,0.1)",
                    color: "#b8941f",
                    border: "1px solid rgba(212,175,55,0.3)",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>

        {/* Posts section */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item md={3} sx={{ display: { xs: "none", sm: "none", lg: "block" } }}>
            <Box sx={sidebarSx}>
              <OnlineFriends onlineUsers={users} />
            </Box>
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            {isOwner && <CreatePost />}
            <ProfilePosts posts={userPosts} />
          </Grid>

          <Grid item md={3} sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
            <Box sx={sidebarSx}>
              <AddFriends />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>
          Edit your info
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField label="First Name" name="firstName" fullWidth size="small" value={updatedUser.firstName || ""} onChange={handleChange} sx={fieldSx} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Last Name" name="lastName" fullWidth size="small" value={updatedUser.lastName || ""} onChange={handleChange} sx={fieldSx} />
              </Grid>
            </Grid>
            <TextField label="Email" name="email" fullWidth size="small" value={updatedUser.email || ""} onChange={handleChange} sx={fieldSx} />
            <TextField label="Origin" name="origin" fullWidth size="small" value={updatedUser.origin || ""} onChange={handleChange} sx={fieldSx} />
            <TextField label="Current Location" name="currentLocation" fullWidth size="small" value={updatedUser.currentLocation || ""} onChange={handleChange} sx={fieldSx} />
            <TextField label="Education" name="education" fullWidth size="small" value={updatedUser.education || ""} onChange={handleChange} sx={fieldSx} />
            <TextField label="Occupation" name="occupation" fullWidth size="small" value={updatedUser.occupation || ""} onChange={handleChange} sx={fieldSx} />
            <TextField
              select
              label="Gender"
              name="gender"
              fullWidth
              size="small"
              value={updatedUser.gender || ""}
              onChange={handleChange}
              SelectProps={{ native: true }}
              sx={fieldSx}
            >
              <option value="">Not specified</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </TextField>
            <TextField
              select
              label="Marital Status"
              name="maritalStatus"
              fullWidth
              size="small"
              value={updatedUser.maritalStatus || ""}
              onChange={handleChange}
              SelectProps={{ native: true }}
              sx={fieldSx}
            >
              <option value="">Not specified</option>
              <option value="Single">Single</option>
              <option value="In a relationship">In a relationship</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
            </TextField>
            <TextField
              label="Birth Date"
              name="birthDate"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={updatedUser.birthDate ? updatedUser.birthDate.split("T")[0] : ""}
              onChange={handleChange}
              sx={fieldSx}
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setEditOpen(false)}
            sx={{ textTransform: "none", color: "#6B7280", borderRadius: "9px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loadingSave}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "9px",
              px: 3,
              background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
              "&:hover": { background: "linear-gradient(135deg, #D4AF37 0%, #b8941f 100%)", color: "#1A1A2E" },
            }}
          >
            {loadingSave ? <CircularProgress size={20} color="inherit" /> : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const sidebarSx = {
  position: "sticky",
  top: 80,
  bgcolor: "#fff",
  borderRadius: "14px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  p: 2,
  maxHeight: "calc(100vh - 100px)",
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": { bgcolor: "#E5E7EB", borderRadius: 2 },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "9px",
    "&:hover fieldset": { borderColor: "#D4AF37" },
    "&.Mui-focused fieldset": { borderColor: "#1A1A2E" },
  },
  "& label.Mui-focused": { color: "#1A1A2E" },
};

export default ProfilePage;
